import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  activateModifier,
  activateModifierGroup,
  archiveModifier,
  archiveModifierGroup,
  getModifierGroups,
  getModifiers,
  suspendModifier,
  suspendModifierGroup,
} from '../../../shared/api/products.js'
import type { Modifier, ModifierGroup } from '../../../shared/types/models.js'
import { availableLifecycleActions, formatPrice, type LifecycleAction } from '../../../shared/utils/catalog.js'
import { ApiError } from '../../../shared/types/api.js'
import ProductStatusBadge from './ProductStatusBadge.js'
import ModifierGroupForm from './ModifierGroupForm.js'
import ModifierForm from './ModifierForm.js'

const ACTION_LABEL: Record<LifecycleAction, string> = {
  suspend: 'products.suspend',
  activate: 'products.activate',
  archive: 'products.archive',
}

const ACTION_CLASS: Record<LifecycleAction, string> = {
  suspend: 'btn btn-ghost',
  activate: 'btn btn-ghost',
  archive: 'btn btn-danger',
}

interface Props {
  organizationId: string
  productId: string | null
  disabled: boolean
  pricePrecisionMode?: string
}

export default function ModifierGroupsEditor({ organizationId, productId, disabled, pricePrecisionMode = 'INTEGER_ONLY' }: Props) {
  const { t } = useTranslation()

  const [groups, setGroups] = useState<ModifierGroup[]>([])
  const [modifiersByGroup, setModifiersByGroup] = useState<Record<string, Modifier[]>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showAddGroup, setShowAddGroup] = useState(false)
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null)
  const [addingModifierGroupId, setAddingModifierGroupId] = useState<string | null>(null)
  const [editingModifierId, setEditingModifierId] = useState<string | null>(null)
  const [busy, setBusy] = useState<{ id: string; action: string } | null>(null)

  const refresh = useCallback(async () => {
    if (!productId) return
    setLoading(true)
    setError('')
    try {
      const gs = (await getModifierGroups(organizationId, productId)) ?? []
      setGroups(gs)
      const entries = await Promise.all(
        gs.map(async g => [g.id, (await getModifiers(organizationId, productId, g.id)) ?? []] as const),
      )
      setModifiersByGroup(Object.fromEntries(entries))
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : t('products.modifierError'))
    } finally {
      setLoading(false)
    }
  }, [organizationId, productId, t])

  useEffect(() => {
    void refresh()
  }, [refresh])

  if (!productId) {
    return (
      <div className="modifier-groups-section">
        <h4>{t('products.modifierGroups')}</h4>
        <p className="page-section-description">{t('products.modifierGroupsCreateNote')}</p>
      </div>
    )
  }
  const pid = productId

  async function handleGroupDone() {
    setShowAddGroup(false)
    setEditingGroupId(null)
    setError('')
    await refresh()
  }

  async function handleModifierDone() {
    setAddingModifierGroupId(null)
    setEditingModifierId(null)
    setError('')
    await refresh()
  }

  async function handleGroupLifecycle(group: ModifierGroup, action: LifecycleAction) {
    if (action === 'archive' && !window.confirm(t('products.archiveGroupConfirm', { name: group.name }))) return
    setError('')
    setBusy({ id: group.id, action })
    try {
      if (action === 'suspend') await suspendModifierGroup(organizationId, pid, group.id)
      else if (action === 'activate') await activateModifierGroup(organizationId, pid, group.id)
      else await archiveModifierGroup(organizationId, pid, group.id)
      await refresh()
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : t('products.modifierError'))
    } finally {
      setBusy(null)
    }
  }

  async function handleModifierLifecycle(modifier: Modifier, action: LifecycleAction) {
    const groupId = modifier.modifierGroupId
    if (action === 'archive' && !window.confirm(t('products.archiveModifierConfirm', { name: modifier.name }))) return
    setError('')
    setBusy({ id: modifier.id, action })
    try {
      if (action === 'suspend') await suspendModifier(organizationId, pid, groupId, modifier.id)
      else if (action === 'activate') await activateModifier(organizationId, pid, groupId, modifier.id)
      else await archiveModifier(organizationId, pid, groupId, modifier.id)
      await refresh()
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : t('products.modifierError'))
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="modifier-groups-section">
      <div className="modifier-groups-header">
        <div>
          <h4>{t('products.modifierGroups')}</h4>
          <p className="page-section-description">{t('products.modifierGroupsHint')}</p>
        </div>
        {!disabled && !showAddGroup && !editingGroupId && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              setError('')
              setShowAddGroup(true)
            }}
          >
            {t('products.addGroup')}
          </button>
        )}
      </div>

      {error && <div className="form-error-banner">{error}</div>}
      {loading && groups.length === 0 && (
        <div className="page-loading">
          <div className="spinner" />
        </div>
      )}

      {showAddGroup && (
        <ModifierGroupForm
          organizationId={organizationId}
          productId={productId}
          group={null}
          disabled={disabled}
          onDone={() => void handleGroupDone()}
          onCancel={() => setShowAddGroup(false)}
        />
      )}

      {groups.length === 0 && !loading ? (
        <div className="modifier-groups-empty">
          <p>{t('products.modifierGroupsEmpty')}</p>
          <p className="page-section-description">{t('products.modifierGroupsEmptyHint')}</p>
          {!disabled && !showAddGroup && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                setError('')
                setShowAddGroup(true)
              }}
            >
              {t('products.addFirstGroup')}
            </button>
          )}
        </div>
      ) : (
        <ul className="modifier-groups-list">
          {groups.map(group => {
            const groupModifiers = modifiersByGroup[group.id] ?? []
            const isBusy = busy?.id === group.id
            const actions = availableLifecycleActions(group.status)
            const editing = editingGroupId === group.id
            const addingModifier = addingModifierGroupId === group.id
            const groupLocked = disabled || group.status === 'ARCHIVED'

            return (
              <li key={group.id} className={`modifier-group-row modifier-group-row--${group.status.toLowerCase()}`}>
                <div className="modifier-group-main">
                  <div className="modifier-group-title">
                    <span className="modifier-group-name">{group.name}</span>
                    {group.isRequired && <span className="required-badge">{t('products.required')}</span>}
                    <ProductStatusBadge status={group.status} />
                  </div>
                  <div className="modifier-group-meta">
                    <span>
                      {t('products.metaSelection', {
                        min: group.min,
                        max: group.max == null ? t('products.unlimited') : group.max,
                      })}
                    </span>
                    <span>{t('products.modifierCount', { count: groupModifiers.length })}</span>
                    <span>{t('products.sortOrderLabel', { order: group.sortOrder })}</span>
                  </div>
                </div>

                {!groupLocked && !editing && (
                  <div className="modifier-group-actions">
                    <button
                      type="button"
                      className="btn btn-ghost"
                      disabled={isBusy}
                      onClick={() => {
                        setError('')
                        setEditingGroupId(group.id)
                      }}
                    >
                      {t('products.edit')}
                    </button>
                    {actions.map(action => (
                      <button
                        key={action}
                        type="button"
                        className={ACTION_CLASS[action]}
                        disabled={isBusy}
                        onClick={() => void handleGroupLifecycle(group, action)}
                      >
                        {isBusy && busy?.action === action ? t('products.processing') : t(ACTION_LABEL[action])}
                      </button>
                    ))}
                  </div>
                )}

                {group.status === 'ARCHIVED' && (
                  <div className="modifier-archived-note">{t('products.groupArchivedNote')}</div>
                )}

                {editing && (
                  <ModifierGroupForm
                    organizationId={organizationId}
                    productId={productId}
                    group={group}
                    disabled={disabled}
                    onDone={() => void handleGroupDone()}
                    onCancel={() => setEditingGroupId(null)}
                  />
                )}

                <div className="modifiers-subsection">
                  <div className="modifiers-header">
                    <span className="modifiers-title">{t('products.modifiers')}</span>
                    {!groupLocked && !addingModifier && (
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => {
                          setError('')
                          setAddingModifierGroupId(group.id)
                        }}
                      >
                        {t('products.addModifier')}
                      </button>
                    )}
                  </div>

                  {groupModifiers.length === 0 ? (
                    <p className="modifiers-empty-hint">{t('products.modifiersEmpty')}</p>
                  ) : (
                    <ul className="modifier-list">
                      {groupModifiers.map(modifier => {
                        const modBusy = busy?.id === modifier.id
                        const modActions = availableLifecycleActions(modifier.status)
                        const modEditing = editingModifierId === modifier.id
                        const modLocked = disabled || modifier.status === 'ARCHIVED'
                        return (
                          <li key={modifier.id} className={`modifier-row modifier-row--${modifier.status.toLowerCase()}`}>
                            <div className="modifier-row-main">
                              <span className="modifier-row-name">{modifier.name}</span>
                              <span className="modifier-row-meta">
                                {formatPrice(modifier.price)}
                                <span> · {t('products.maxQuantityShort', { n: modifier.maxQuantity })}</span>
                                <span> · {t('products.sortOrderLabel', { order: modifier.sortOrder })}</span>
                              </span>
                            </div>

                            <ProductStatusBadge status={modifier.status} />

                            {!modLocked && !modEditing && (
                              <div className="modifier-row-actions">
                                <button
                                  type="button"
                                  className="btn btn-ghost"
                                  disabled={modBusy}
                                  onClick={() => {
                                    setError('')
                                    setEditingModifierId(modifier.id)
                                  }}
                                >
                                  {t('products.edit')}
                                </button>
                                {modActions.map(action => (
                                  <button
                                    key={action}
                                    type="button"
                                    className={ACTION_CLASS[action]}
                                    disabled={modBusy}
                                    onClick={() => void handleModifierLifecycle(modifier, action)}
                                  >
                                    {modBusy && busy?.action === action
                                      ? t('products.processing')
                                      : t(ACTION_LABEL[action])}
                                  </button>
                                ))}
                              </div>
                            )}

                            {modifier.status === 'ARCHIVED' && (
                              <span className="modifier-archived-note">{t('products.modifierArchivedNote')}</span>
                            )}

                            {modEditing && (
                              <ModifierForm
                                organizationId={organizationId}
                                productId={productId}
                                groupId={modifier.modifierGroupId}
                                modifier={modifier}
                                disabled={disabled}
                                pricePrecisionMode={pricePrecisionMode}
                                onDone={() => void handleModifierDone()}
                                onCancel={() => setEditingModifierId(null)}
                              />
                            )}
                          </li>
                        )
                      })}
                    </ul>
                  )}

                  {addingModifier && (
                    <ModifierForm
                      organizationId={organizationId}
                      productId={productId}
                      groupId={group.id}
                      modifier={null}
                      disabled={disabled}
                      pricePrecisionMode={pricePrecisionMode}
                      onDone={() => void handleModifierDone()}
                      onCancel={() => setAddingModifierGroupId(null)}
                    />
                  )}
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}